import {
  BaseTexture,
  Color3,
  Color4,
  DynamicTexture,
  Material,
  MultiMaterial,
  PBRMaterial,
  StandardMaterial,
  Texture,
} from '@babylonjs/core';
import { GrassProceduralTexture, MarbleProceduralTexture, RoadProceduralTexture } from '@babylonjs/procedural-textures';
import { ColorPaletteGenerator } from '@martinlaxenaire/color-palette-generator';
import { Game } from './Game';

export class MaterialService {
  game: Game;

  get textureMarble(): MarbleProceduralTexture {
    if (!this._textureMarble) {
      this._textureMarble = new MarbleProceduralTexture('marbleTexture', 1024, this.game.scene);
    }
    return this._textureMarble;
  }

  get textureAsphalt(): RoadProceduralTexture {
    if (!this._textureAsphalt) {
      this._textureAsphalt = new RoadProceduralTexture('asphaltTexture', 1024, this.game.scene);
    }
    return this._textureAsphalt;
  }

  get textureGrass(): GrassProceduralTexture {
    if (!this._textureGrass) {
      this._textureGrass = new GrassProceduralTexture('grassTexture', 1024, this.game.scene);
    }
    return this._textureGrass;
  }

  private _textureMarble: MarbleProceduralTexture;
  private _textureAsphalt: RoadProceduralTexture;
  private _textureGrass: GrassProceduralTexture;

  private simpleGlassMaterial: Material;

  colorMaterials: Map<string, StandardMaterial> = new Map();

  store: Map<string, Material> = new Map();

  static palette(): string[] {
    return ['Blue', 'Red', 'Green', 'Yellow', 'Purple', 'Magenta', 'Orange'];
  }

  static colorByName(name: string): Color4 {
    return MaterialService['color' + name];
  }

  static colorBlue = Color4.FromColor3(Color3.Blue(), 1.0);
  static colorRed = Color4.FromColor3(Color3.Red(), 1.0);
  static colorGreen = Color4.FromColor3(Color3.Green(), 1.0);
  static colorYellow = Color4.FromColor3(Color3.Yellow(), 1.0);
  static colorPurple = Color4.FromColor3(Color3.Purple(), 1.0);
  static colorMagenta = Color4.FromColor3(Color3.Magenta(), 1.0);
  static colorOrange = Color4.FromColor3(new Color3(0.8, 0.8, 0), 1.0);
  static colorWhite = Color4.FromColor3(Color3.White(), 1.0);

  constructor(game: Game) {
    this.game = game;

    for (const color of MaterialService.palette()) {
      this.colorMaterials.set(color, this.createColorMaterial(color, MaterialService.colorByName(color)));
    }
  }

  saveMaterials(materials: Material[]): string[] {
    const names: string[] = [];
    for (const material of materials) {
      names.push(this.saveMaterial(material));
    }
    return names;
  }

  saveMaterial(material: Material): string {
    let name = material.name;
    if (!name) {
      name = 'material_' + this.store.size;
    }

    this.store.set(name, material);
    return name;
  }

  getMaterial(name: string): Material {
    return this.store.get(name);
  }

  getPaletteGenerator(baseColor: Color3, rand: () => number): ColorPaletteGenerator {
    const palette = new ColorPaletteGenerator({
      rand,
      precision: 6,
      baseColor: baseColor.toHexString(),
    });
    return palette;
  }

  createGlassMaterial(reflectionTexture: BaseTexture): PBRMaterial {
    const glass = new PBRMaterial('glass', this.game.scene);
    glass.reflectionTexture = reflectionTexture;
    glass.alpha = 0.25;
    glass.directIntensity = 0.5;
    glass.environmentIntensity = 0.75;
    glass.microSurface = 1;
    glass.metallic = 1;
    glass.roughness = 0.05;
    glass.reflectionColor = new Color3(1, 1, 1);
    glass.reflectivityColor = new Color3(1, 1, 1);
    glass.albedoColor = new Color3(1, 1, 1);
    return glass;
  }

  createRandomColorMaterials(baseColor: Color3, length: number, rand: () => number): StandardMaterial[] {
    const materials: StandardMaterial[] = [];
    const palette = this.getPaletteGenerator(baseColor, rand);
    const randomPalette = palette.getRandomPalette({ length });
    for (let i = 0; i < randomPalette.length; i++) {
      const colorModel = randomPalette[i];
      const color = Color3.FromHexString(colorModel.hex);
      const material = this.createColorMaterial('color-' + colorModel.hex, color);
      materials.push(material);
    }
    return materials;
  }

  colorMaterial(name: string): StandardMaterial {
    return this.colorMaterials.get(name);
  }

  createTexture(url: string, uScale: number = 1, vScale: number = 1): Texture {
    const texture = new Texture(url, this.game.scene);
    return this.scaleTexture(texture, uScale, vScale);
  }

  createColorMaterial(name: string, color: Color3 | Color4, alpha: number = 1): StandardMaterial {
    if (color instanceof Color4) {
      alpha = color.a;
      color = new Color3(color.r, color.g, color.b);
    }
    const material = new StandardMaterial(name, this.game.scene);
    material.diffuseColor = color;
    material.alpha = alpha;
    material.specularColor = color;
    return material;
  }

  createPBRColorMaterial(name: string, color: Color3 | Color4, alpha: number = 1): PBRMaterial {
    if (color instanceof Color4) {
      alpha = color.a;
      color = new Color3(color.r, color.g, color.b);
    }
    const material = new PBRMaterial(name, this.game.scene);
    material.albedoColor = color;
    material.alpha = alpha;
    return material;
  }

  createRandomColorMaterial(alpha: number = 0.5): StandardMaterial {
    return this.createColorMaterial('randommaterial', Color3.Random(), alpha);
  }

  createPBRMaterial(
    name: string,
    albedoTexture: string | Texture,
    uScale: number = 1,
    vScale: number = 1,
    uOffset?: number,
    vOffset?: number,
    bumpTexture?: string | Texture,
    metallicTexture?: string | Texture
  ): PBRMaterial {
    const material = new PBRMaterial(name, this.game.scene);
    material.metallic = 0.0;
    material.roughness = 1.0;

    if (typeof albedoTexture === 'string') {
      albedoTexture = this.scaleTexture(this.createTexture(albedoTexture), uScale, vScale, uOffset, vOffset);
    }

    material.albedoTexture = albedoTexture;

    if (bumpTexture) {
      if (typeof bumpTexture === 'string') {
        bumpTexture = this.scaleTexture(this.createTexture(bumpTexture), uScale, vScale, uOffset, vOffset);
      }
      material.bumpTexture = bumpTexture;
    }

    if (metallicTexture) {
      if (typeof metallicTexture === 'string') {
        metallicTexture = this.scaleTexture(this.createTexture(metallicTexture), uScale, vScale, uOffset, vOffset);
      }
      material.metallicTexture = metallicTexture;
      material.useAmbientOcclusionFromMetallicTextureRed = true;
      material.useRoughnessFromMetallicTextureGreen = true;
      material.useMetallnessFromMetallicTextureBlue = true;
    }

    return material;
  }

  createMaterial(
    name: string,
    texture: string | Texture,
    uScale?: number,
    vScale?: number,
    uOffset?: number,
    vOffset?: number
  ): StandardMaterial {
    if (typeof texture === 'string') {
      texture = this.createTexture(texture);
    }
    texture = this.scaleTexture(texture, uScale, vScale, uOffset, vOffset);
    const material = new StandardMaterial(name, this.game.scene);
    material.diffuseTexture = texture;
    material.specularColor = new Color3(0, 0, 0);
    return material;
  }

  scaleTexture(texture: Texture, uScale: number = 1, vScale: number = 1, uOffset: number = 0, vOffset: number = 0): Texture {
    texture.uScale = uScale; //Repeat on the Vertical Axes
    texture.vScale = vScale; //Repeat  on the Horizontal Axes
    texture.uOffset = uOffset;
    texture.vOffset = vOffset;
    return texture;
  }

  createSimpleGlassMaterial(): Material {
    if (!this.simpleGlassMaterial) {
      const mat = this.createPBRColorMaterial('simpleGlass', new Color3(0.6, 0.6, 1), 0.5);
      mat.metallic = 0.9;
      mat.roughness = 0.1;
      mat.directIntensity = 1;
      mat.environmentIntensity = 0.1;
      this.simpleGlassMaterial = mat;
    }
    return this.simpleGlassMaterial;
  }

  createTextTexture(width: number, height: number, text: string, fontType = 'Arial'): DynamicTexture {
    //set width and height for dynamic texture using same multiplier
    const DTWidth = width * 60;
    const DTHeight = height * 60;

    //create dynamic texture
    const dynamicTexture = new DynamicTexture('TextTexture', { width: DTWidth, height: DTHeight }, this.game.scene);

    //check width of text for given font type at any size of font
    const ctx = dynamicTexture.getContext();
    const size = 12; //any value will work
    ctx.font = size + 'px ' + fontType;
    const textWidth = ctx.measureText(text).width;

    //calculate ratio of text width to size of font used
    const ratio = textWidth / size;

    //set font to be actually used to write text on dynamic texture
    const font_size = Math.floor(DTWidth / (ratio * 1)); //size of multiplier (1) can be adjusted, increase for smaller text
    const font = font_size + 'px ' + fontType;

    //draw text
    dynamicTexture.drawText(text, null, null, font, '#000000', '#ffffff', true);

    return dynamicTexture;
  }

  createTextMaterial(width: number, height: number, text: string, backgroundColor: Color3, fontType = 'Arial'): StandardMaterial {
    const mat = new StandardMaterial('mat', this.game.scene);
    mat.diffuseTexture = this.createTextTexture(width, height, text, fontType);
    mat.diffuseColor = backgroundColor;
    return mat;
  }

  setReflectivity(material: Material, color: Color3, metallic: number = 0, roughness: number = 1): void {
    if (material instanceof PBRMaterial) {
      material.metallic = metallic;
      material.roughness = roughness;
      material.reflectivityColor = color;
    }

    if (material instanceof MultiMaterial) {
      material.getChildren().forEach((child) => {
        if (child instanceof PBRMaterial) {
          child.metallic = metallic;
          child.roughness = roughness;
          child.reflectivityColor = color;
        }
      });
    }
  }
}
